import React, { useState, useRef, useEffect } from 'react';
import Layout from '../../Component/Layout';
import {
    FaIndustry, FaBullseye, FaClipboardList, FaClock,
    FaShieldAlt, FaRupeeSign, FaTachometerAlt, FaCalendarAlt
} from 'react-icons/fa';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, ComposedChart
} from 'recharts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// ==========================================
// DATE RANGE HELPERS (same logic as CFO / CSO Dashboard)
// ==========================================

function getCurrentMonthRange() {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { start, end };
}

function formatRangeLabel(start, end) {
    if (!start || !end) return 'Select date range';
    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} – ${endStr}`;
}

function toApiDateString(date) {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

// ==========================================
// STATIC / MOCK DATA
// ==========================================
const kpis = [
    { label: 'Total Production (This Month)', value: '1,24,560 km', change: '+12.6% vs Apr \'25', up: true, icon: FaIndustry, bg: '#2563eb' },
    { label: 'Production Achievement (Actual vs Plan)', value: '102.6%', change: '+2.6 pp vs Apr \'25', up: true, icon: FaBullseye, bg: '#16a34a' },
    { label: 'Orders In Progress', value: '82,450 km', change: '+9.8% vs Apr \'25', up: true, icon: FaClipboardList, bg: '#7c3aed' },
    { label: 'On-Time Delivery (OTD)', value: '92.3%', change: '+4.2 pp vs Apr \'25', up: true, icon: FaClock, bg: '#ea580c' },
    { label: 'First Pass Yield (FPY)', value: '96.4%', change: '+1.8 pp vs Apr \'25', up: true, icon: FaShieldAlt, bg: '#dc2626' },
    { label: 'Operating Cost / km', value: '₹ 98.42', change: '-4.3% vs Apr \'25', up: false, icon: FaRupeeSign, bg: '#0284c7' },
    { label: 'Overall Efficiency (OE)', value: '78.6%', change: '+3.7 pp vs Apr \'25', up: true, icon: FaTachometerAlt, bg: '#f59e0b' },
];

const plannedVsActual = [
    { day: 'May 1', planned: 4050, actual: 3900, achievement: 96 },
    { day: 'May 3', planned: 4050, actual: 4150, achievement: 102 },
    { day: 'May 6', planned: 4050, actual: 4000, achievement: 99 },
    { day: 'May 8', planned: 4050, actual: 4220, achievement: 104 },
    { day: 'May 11', planned: 4050, actual: 3980, achievement: 98 },
    { day: 'May 13', planned: 4050, actual: 4180, achievement: 103 },
    { day: 'May 16', planned: 4050, actual: 4090, achievement: 101 },
    { day: 'May 18', planned: 4050, actual: 3950, achievement: 97 },
    { day: 'May 21', planned: 4050, actual: 4210, achievement: 104 },
    { day: 'May 23', planned: 4050, actual: 4020, achievement: 99 },
    { day: 'May 26', planned: 4050, actual: 4160, achievement: 103 },
    { day: 'May 28', planned: 4050, actual: 6210, achievement: 153 },
    { day: 'May 31', planned: 4050, actual: 4080, achievement: 101 },
];

const productionSummary = { planned: 121500, actual: 124560, achievement: '102.6%', avgDailyPlan: 3919, avgDailyActual: 4018, bestDay: "May 28 (6,210)" };

const productGroup = [
    { name: 'Power Cables', value: 52310, pct: 42.0, color: '#2563eb' },
    { name: 'Control Cables', value: 29480, pct: 23.7, color: '#16a34a' },
    { name: 'Instrumentation Cables', value: 19250, pct: 15.4, color: '#f97316' },
    { name: 'Wires & Flexible Cables', value: 13100, pct: 10.5, color: '#7c3aed' },
    { name: 'Others', value: 10420, pct: 8.4, color: '#9ca3af' },
];
const productionTotal = 124560;

const otdTrend = [
    { day: 'May 1', otd: 82 }, { day: 'May 4', otd: 85 }, { day: 'May 7', otd: 83 },
    { day: 'May 10', otd: 88 }, { day: 'May 13', otd: 90 }, { day: 'May 16', otd: 87 },
    { day: 'May 19', otd: 91 }, { day: 'May 22', otd: 89 }, { day: 'May 25', otd: 93 },
    { day: 'May 28', otd: 90 }, { day: 'May 31', otd: 92.3 },
];
const otdSummary = { thisMonth: '92.3%', lastMonth: '88.1%', ytd: '90.2%' };

const oeBreakdown = [
    { component: 'Availability (A)', pct: 87.2, change: '+3.2' },
    { component: 'Performance (P)', pct: 82.5, change: '+2.7' },
    { component: 'Quality (Q)', pct: 96.4, change: '+1.8' },
];
const oeOverall = { pct: 78.6, change: '+3.7' };
const oeGaugeData = [
    { name: 'OE', value: 78.6, color: '#dc2626' },
    { name: 'rest', value: 21.4, color: '#f3f4f6' },
];

const machineUtilisation = [
    { name: 'Extrusion Line 1', pct: 82.1, change: '+4.8' },
    { name: 'Extrusion Line 2', pct: 79.3, change: '+3.6' },
    { name: 'Stranding', pct: 76.8, change: '+2.1' },
    { name: 'Armouring', pct: 74.5, change: '+3.9' },
    { name: 'Braiding', pct: 73.2, change: '+1.8' },
    { name: 'Sheathing Line 1', pct: 81.6, change: '+5.2' },
    { name: 'Sheathing Line 2', pct: 78.4, change: '+2.7' },
];
const avgUtilisation = { pct: 78.1, change: '+3.4' };

const shopFloorStats = [
    { label: 'WIP Inventory', value: '42,860 km', sub: '₹ 9.46 Cr', cover: '10 Days Cover' },
    { label: 'Raw Material Excess', value: '18,740 km', sub: '₹ 4.12 Cr', cover: '24 Days Cover' },
    { label: 'Excess Scrap', value: '2,890 km', sub: '₹ 0.68 Cr', cover: '2.1% of Production' },
];
const topWipByWorkCenter = [
    { name: 'Extrusion Line 1', wip: 12450, cover: 12.3 },
    { name: 'Stranding', wip: 9860, cover: 9.2 },
    { name: 'Armouring', wip: 7250, cover: 8.1 },
    { name: 'Sheathing Line 1', wip: 6980, cover: 7.6 },
    { name: 'Others', wip: 6320, cover: 6.3 },
];
const wipTotal = { wip: 42860, cover: 10.0 };

const ordersDelivery = [
    { name: 'On Time', value: 67050, pct: 81.4, color: '#16a34a' },
    { name: 'At Risk', value: 9550, pct: 11.6, color: '#f59e0b' },
    { name: 'Delayed', value: 5850, pct: 7.0, color: '#dc2626' },
];
const ordersDeliveryTotal = 82450;

const ordersInProgress = [
    { type: 'Firm Orders', count: 42, qty: 46750, pct: 56.7, confDate: 'Jun 07, 2025' },
    { type: 'Planned Orders', count: 28, qty: 21300, pct: 25.8, confDate: 'Jun 15, 2025' },
    { type: 'Customer Forecast', count: 15, qty: 14400, pct: 17.5, confDate: 'Jun 21, 2025' },
];
const ordersInProgressTotal = { count: 85, qty: 82450, pct: 100 };

const delayedOrders = [
    { customer: 'L&T Construction', orderNo: 'SO-250412', qty: 6520, delay: 7, confDate: 'May 28, 2025' },
    { customer: 'Reliance Industries', orderNo: 'SO-250398', qty: 5840, delay: 6, confDate: 'May 29, 2025' },
    { customer: 'Tata Power', orderNo: 'SO-250421', qty: 5100, delay: 5, confDate: 'May 31, 2025' },
    { customer: 'Adani Energy', orderNo: 'SO-250407', qty: 4620, delay: 4, confDate: 'Jun 01, 2025' },
    { customer: 'Vedanta Ltd.', orderNo: 'SO-250415', qty: 3280, delay: 3, confDate: 'Jun 02, 2025' },
];

const alerts = [
    { level: 'High', text: 'Sheathing Line 2 – OE below 75% for 3 consecutive days', time: '2h ago' },
    { level: 'Medium', text: 'Copper rod inventory – 6 days of cover remaining', time: '3h ago' },
    { level: 'Medium', text: '5 Orders delayed by more than 3 days', time: '4h ago' },
    { level: 'Low', text: '3 Quality complaints open for more than 7 days', time: '6h ago' },
    { level: 'Low', text: 'Preventive maintenance pending for 4 machines', time: '8h ago' },
];

const alertStyle = {
    High: { background: '#fee2e2', color: '#dc2626' },
    Medium: { background: '#fef3c7', color: '#d97706' },
    Low: { background: '#dbeafe', color: '#2563eb' },
};

// ==========================================
// PLAIN INLINE STYLES (no Tailwind needed)
// ==========================================
const s = {
    page: { padding: 24, background: '#f8fafc', fontFamily: 'Inter, Arial, sans-serif' },
    headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
    title: { fontSize: 26, fontWeight: 700, color: '#111827', margin: 0 },
    subtitle: { fontSize: 13, color: '#6b7280', margin: '2px 0 0' },
    toolbarBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', fontSize: 13, color: '#374151', cursor: 'pointer' },
    toolbarRow: { display: 'flex', gap: 10, flexWrap: 'wrap' },
    card: { background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
    cardHeader: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '16px 20px 12px' },
    cardTitle: { fontSize: 15, fontWeight: 600, color: '#1f2937', margin: 0 },
    cardSubtitle: { fontSize: 12, color: '#9ca3af' },
    kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 16, marginBottom: 20 },
    kpiIcon: (bg) => ({ width: 40, height: 40, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 16, marginBottom: 10 }),
    kpiLabel: { fontSize: 12.5, color: '#6b7280', marginBottom: 4 },
    kpiValue: { fontSize: 21, fontWeight: 700, color: '#111827', marginBottom: 4 },
    kpiChangeUp: { fontSize: 12, color: '#16a34a', fontWeight: 500 },
    kpiChangeDown: { fontSize: 12, color: '#dc2626', fontWeight: 500 },
    grid3: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 20 },
    grid4: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 20 },
    grid1_2: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 20 },
    table: { width: '100%', fontSize: 13, borderCollapse: 'collapse' },
    th: { fontSize: 11, color: '#9ca3af', textAlign: 'left', fontWeight: 500, paddingBottom: 8 },
    thRight: { fontSize: 11, color: '#9ca3af', textAlign: 'right', fontWeight: 500, paddingBottom: 8 },
    td: { padding: '6px 0', color: '#374151', borderTop: '1px solid #f3f4f6' },
    tdRight: { padding: '6px 0', textAlign: 'right', color: '#374151', borderTop: '1px solid #f3f4f6' },
    viewAll: { padding: '12px 20px', borderTop: '1px solid #f3f4f6', color: '#2563eb', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
    badge: (style) => ({ fontSize: 11, padding: '2px 10px', borderRadius: 999, fontWeight: 500, ...style }),
    progressTrack: { width: '100%', height: 7, background: '#f3f4f6', borderRadius: 999, overflow: 'hidden' },
    progressFill: (pct, color) => ({ width: `${Math.min(pct, 100)}%`, height: '100%', background: color, borderRadius: 999 }),
};

function fmtKm(n) { return n.toLocaleString('en-IN'); }

// ==========================================
// COMPONENT
// ==========================================
function COODashboard() {
    const { start: defaultStart, end: defaultEnd } = getCurrentMonthRange();
    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(defaultEnd);
    const [showPicker, setShowPicker] = useState(false);
    const pickerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setShowPicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
        if (start && end) {
            setShowPicker(false);
        }
    };

    return (
        <Layout defaultSidebarOpen={false}>
            <div style={s.page}>
                {/* Header */}
                <div style={s.headerRow}>
                    <div>
                        <h1 style={s.title}>COO Dashboard</h1>
                        <p style={s.subtitle}>Operations Performance at a Glance</p>
                    </div>
                    <div style={s.toolbarRow}>
                        <div style={{ position: 'relative' }} ref={pickerRef}>
                            <button
                                style={s.toolbarBtn}
                                onClick={() => setShowPicker((prev) => !prev)}
                            >
                                <FaCalendarAlt size={13} color="#6b7280" />
                                {formatRangeLabel(startDate, endDate)} ▾
                            </button>

                            {showPicker && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 'calc(100% + 8px)',
                                        left: 0,
                                        zIndex: 100,
                                        background: '#fff',
                                        borderRadius: 10,
                                        boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                                        border: '1px solid #e5e7eb',
                                        padding: 10,
                                    }}
                                >
                                    <DatePicker
                                        selectsRange
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={handleDateChange}
                                        inline
                                        monthsShown={2}
                                    />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                                        <button
                                            onClick={() => {
                                                const { start, end } = getCurrentMonthRange();
                                                setStartDate(start);
                                                setEndDate(end);
                                                setShowPicker(false);
                                            }}
                                            style={{
                                                background: 'transparent',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: 8,
                                                padding: '6px 12px',
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: '#374151',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            This Month
                                        </button>
                                        <button
                                            onClick={() => setShowPicker(false)}
                                            style={{
                                                background: '#2563eb',
                                                border: 'none',
                                                borderRadius: 8,
                                                padding: '6px 14px',
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: '#fff',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <button style={s.toolbarBtn}>All Plants ▾</button>
                        <button style={s.toolbarBtn}>🔄 Refresh</button>
                        <button style={s.toolbarBtn}>⬇ Export</button>
                    </div>
                </div>

                {/* KPI Cards */}
                <div style={s.kpiGrid}>
                    {kpis.map((k) => {
                        const Icon = k.icon;
                        return (
                            <div key={k.label} style={{ ...s.card, padding: 18 }}>
                                <div style={s.kpiIcon(k.bg)}><Icon /></div>
                                <div style={s.kpiLabel}>{k.label}</div>
                                <div style={s.kpiValue}>{k.value}</div>
                                <div style={k.up ? s.kpiChangeUp : s.kpiChangeDown}>
                                    {k.up ? '▲' : '▼'} {k.change}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Row 1: Planned vs Actual / Production by Product Group / OTD Trend */}
                <div style={s.grid3}>
                    <div style={s.card}>
                        <div style={s.cardHeader}><h3 style={s.cardTitle}>Planned vs Actual Production</h3></div>
                        <div style={{ height: 220, padding: '0 12px 8px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={plannedVsActual}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                    <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                                    <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                                    <Tooltip />
                                    <Bar yAxisId="left" dataKey="planned" fill="#e5e7eb" name="Planned (km)" barSize={12} radius={[3, 3, 0, 0]} />
                                    <Bar yAxisId="left" dataKey="actual" fill="#2563eb" name="Actual (km)" barSize={12} radius={[3, 3, 0, 0]} />
                                    <Line yAxisId="right" type="monotone" dataKey="achievement" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} name="Achievement (%)" />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                        <div style={{ padding: '0 20px 16px', overflowX: 'auto' }}>
                            <table style={s.table}>
                                <thead>
                                    <tr>
                                        <th style={s.th}>Planned (km)</th>
                                        <th style={s.th}>Actual (km)</th>
                                        <th style={s.th}>Achievement</th>
                                        <th style={s.th}>Avg. Daily Plan</th>
                                        <th style={s.th}>Avg. Daily Actual</th>
                                        <th style={s.th}>Best Day (Actual)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td style={s.td}>{fmtKm(productionSummary.planned)}</td>
                                        <td style={s.td}>{fmtKm(productionSummary.actual)}</td>
                                        <td style={s.td}>{productionSummary.achievement}</td>
                                        <td style={s.td}>{fmtKm(productionSummary.avgDailyPlan)}</td>
                                        <td style={s.td}>{fmtKm(productionSummary.avgDailyActual)}</td>
                                        <td style={s.td}>{productionSummary.bestDay}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div style={s.card}>
                        <div style={s.cardHeader}>
                            <h3 style={s.cardTitle}>Production by Product Group</h3>
                            <span style={s.cardSubtitle}>(This Month)</span>
                        </div>
                        <div style={{ height: 190, position: 'relative' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={productGroup} dataKey="value" nameKey="name" innerRadius={55} outerRadius={80} paddingAngle={2}>
                                        {productGroup.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                            <div style={{ position: 'absolute', top: '46%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
                                <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{fmtKm(productionTotal)} km</div>
                                <div style={{ fontSize: 10, color: '#9ca3af' }}>Total Production</div>
                            </div>
                        </div>
                        <div style={{ padding: '0 20px 16px' }}>
                            {productGroup.map((p) => (
                                <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#4b5563' }}>
                                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, display: 'inline-block' }} />
                                        {p.name}
                                    </span>
                                    <span style={{ color: '#111827', fontWeight: 500 }}>{fmtKm(p.value)} km ({p.pct.toFixed(1)}%)</span>
                                </div>
                            ))}
                        </div>
                        <div style={s.viewAll}>View Production Report →</div>
                    </div>

                    <div style={s.card}>
                        <div style={s.cardHeader}>
                            <h3 style={s.cardTitle}>On-Time Delivery Trend</h3>
                            <span style={s.cardSubtitle}>(%)</span>
                        </div>
                        <div style={{ height: 190, padding: '0 12px 8px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={otdTrend}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                    <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                                    <YAxis domain={[70, 100]} tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                                    <Tooltip />
                                    <Line type="monotone" dataKey="otd" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} name="OTD (%)" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                        <div style={{ display: 'flex', gap: 8, padding: '0 20px 16px' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 11, color: '#9ca3af' }}>OTD This Month</div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{otdSummary.thisMonth}</div>
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 11, color: '#9ca3af' }}>OTD Last Month</div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{otdSummary.lastMonth}</div>
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 11, color: '#9ca3af' }}>OTD YTD</div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{otdSummary.ytd}</div>
                            </div>
                        </div>
                        <div style={s.viewAll}>View Delivery Performance →</div>
                    </div>
                </div>

                {/* Row 2: OE Breakdown / Machine Utilisation / Shop Floor Inventory / Orders-Delivery */}
                <div style={s.grid4}>
                    <div style={s.card}>
                        <div style={s.cardHeader}>
                            <h3 style={s.cardTitle}>Overall Efficiency (OE) Breakdown</h3>
                        </div>
                        <div style={{ height: 140, position: 'relative' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={oeGaugeData}
                                        dataKey="value"
                                        startAngle={180}
                                        endAngle={0}
                                        innerRadius={60}
                                        outerRadius={85}
                                        cy="100%"
                                    >
                                        {oeGaugeData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                            <div style={{ position: 'absolute', top: '62%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
                                <div style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}>{oeOverall.pct}%</div>
                                <div style={{ fontSize: 10, color: '#9ca3af' }}>Overall Efficiency</div>
                            </div>
                            <div style={{ position: 'absolute', bottom: 6, left: 14, fontSize: 10, color: '#9ca3af' }}>0%</div>
                            <div style={{ position: 'absolute', bottom: 6, right: 14, fontSize: 10, color: '#9ca3af' }}>100%</div>
                        </div>
                        <div style={{ padding: '4px 20px 16px', overflowX: 'auto' }}>
                            <table style={s.table}>
                                <thead>
                                    <tr>
                                        <th style={s.th}>Component</th>
                                        <th style={s.thRight}>%</th>
                                        <th style={s.thRight}>vs Apr '25 (pp)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {oeBreakdown.map((o) => (
                                        <tr key={o.component}>
                                            <td style={s.td}>{o.component}</td>
                                            <td style={s.tdRight}>{o.pct.toFixed(1)}%</td>
                                            <td style={{ ...s.tdRight, color: '#16a34a' }}>▲ {o.change}</td>
                                        </tr>
                                    ))}
                                    <tr style={{ fontWeight: 600, color: '#111827' }}>
                                        <td style={s.td}>OE (A x P x Q)</td>
                                        <td style={s.tdRight}>{oeOverall.pct}%</td>
                                        <td style={{ ...s.tdRight, color: '#16a34a' }}>▲ {oeOverall.change}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div style={s.card}>
                        <div style={s.cardHeader}>
                            <h3 style={s.cardTitle}>Machine Utilisation</h3>
                            <span style={s.cardSubtitle}>(Based on Loading Time)</span>
                        </div>
                        <div style={{ padding: '4px 20px 4px' }}>
                            {machineUtilisation.map((m) => (
                                <div key={m.name} style={{ marginBottom: 10 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                                        <span style={{ color: '#4b5563' }}>{m.name}</span>
                                        <span style={{ color: '#111827', fontWeight: 500 }}>{m.pct.toFixed(1)}% <span style={{ color: '#16a34a', fontWeight: 400 }}>▲ {m.change}</span></span>
                                    </div>
                                    <div style={s.progressTrack}>
                                        <div style={s.progressFill(m.pct, '#2563eb')} />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 20px 16px', borderTop: '1px solid #f3f4f6', marginTop: 4 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>Average Utilisation</span>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{avgUtilisation.pct}% <span style={{ color: '#16a34a', fontWeight: 500 }}>▲ {avgUtilisation.change}</span></span>
                        </div>
                    </div>

                    <div style={s.card}>
                        <div style={s.cardHeader}><h3 style={s.cardTitle}>Shop Floor Inventory &amp; Scrap</h3></div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 20px 12px' }}>
                            {shopFloorStats.map((st) => (
                                <div key={st.label} style={{ background: '#f9fafb', borderRadius: 8, padding: 12 }}>
                                    <div style={{ fontSize: 11, color: '#6b7280' }}>{st.label}</div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{st.value}</div>
                                    <div style={{ fontSize: 11, color: '#6b7280' }}>{st.sub} · {st.cover}</div>
                                </div>
                            ))}
                        </div>
                        <div style={{ padding: '0 20px 8px' }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Top WIP by Work Center (km)</div>
                            <table style={s.table}>
                                <thead>
                                    <tr>
                                        <th style={s.th}>Work Center</th>
                                        <th style={s.thRight}>WIP (km)</th>
                                        <th style={s.thRight}>Days Cover</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {topWipByWorkCenter.map((w) => (
                                        <tr key={w.name}>
                                            <td style={s.td}>{w.name}</td>
                                            <td style={s.tdRight}>{fmtKm(w.wip)}</td>
                                            <td style={s.tdRight}>{w.cover}</td>
                                        </tr>
                                    ))}
                                    <tr style={{ fontWeight: 600, color: '#111827' }}>
                                        <td style={s.td}>Total</td>
                                        <td style={s.tdRight}>{fmtKm(wipTotal.wip)}</td>
                                        <td style={s.tdRight}>{wipTotal.cover}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div style={s.card}>
                        <div style={s.cardHeader}><h3 style={s.cardTitle}>Orders – Delivery Commitment</h3></div>
                        <div style={{ height: 190, position: 'relative' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={ordersDelivery} dataKey="value" nameKey="name" innerRadius={55} outerRadius={80} paddingAngle={2}>
                                        {ordersDelivery.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                            <div style={{ position: 'absolute', top: '46%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
                                <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{fmtKm(ordersDeliveryTotal)} km</div>
                                <div style={{ fontSize: 10, color: '#9ca3af' }}>Total Orders<br />In Progress</div>
                            </div>
                        </div>
                        <div style={{ padding: '0 20px 16px' }}>
                            {ordersDelivery.map((o) => (
                                <div key={o.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#4b5563' }}>
                                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: o.color, display: 'inline-block' }} />
                                        {o.name}
                                    </span>
                                    <span style={{ color: '#111827', fontWeight: 500 }}>{o.pct.toFixed(1)}% ({fmtKm(o.value)} km)</span>
                                </div>
                            ))}
                        </div>
                        <div style={s.viewAll}>View Order Status →</div>
                    </div>
                </div>

                {/* Row 3: Orders In Progress / Top Delayed Orders / Alerts */}
                <div style={s.grid3}>
                    <div style={s.card}>
                        <div style={s.cardHeader}>
                            <h3 style={s.cardTitle}>Orders In Progress</h3>
                            <span style={s.cardSubtitle}>(km)</span>
                        </div>
                        <div style={{ padding: '0 20px 8px', overflowX: 'auto' }}>
                            <table style={s.table}>
                                <thead>
                                    <tr>
                                        <th style={s.th}>Order Type</th>
                                        <th style={s.thRight}>No. of Orders</th>
                                        <th style={s.thRight}>Order Quantity (km)</th>
                                        <th style={s.thRight}>% of Total</th>
                                        <th style={s.th}>Avg. Conf. Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ordersInProgress.map((o) => (
                                        <tr key={o.type}>
                                            <td style={s.td}>{o.type}</td>
                                            <td style={s.tdRight}>{o.count}</td>
                                            <td style={s.tdRight}>{fmtKm(o.qty)}</td>
                                            <td style={s.tdRight}>{o.pct.toFixed(1)}%</td>
                                            <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{o.confDate}</td>
                                        </tr>
                                    ))}
                                    <tr style={{ fontWeight: 600, color: '#111827' }}>
                                        <td style={s.td}>Total</td>
                                        <td style={s.tdRight}>{ordersInProgressTotal.count}</td>
                                        <td style={s.tdRight}>{fmtKm(ordersInProgressTotal.qty)}</td>
                                        <td style={s.tdRight}>{ordersInProgressTotal.pct}%</td>
                                        <td style={s.td}></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div style={s.viewAll}>View All Orders →</div>
                    </div>

                    <div style={s.card}>
                        <div style={s.cardHeader}><h3 style={s.cardTitle}>Top Delayed / At Risk Orders</h3></div>
                        <div style={{ padding: '0 20px 8px', overflowX: 'auto' }}>
                            <table style={s.table}>
                                <thead>
                                    <tr>
                                        <th style={s.th}>Customer</th>
                                        <th style={s.th}>Order No.</th>
                                        <th style={s.thRight}>Qty (km)</th>
                                        <th style={s.thRight}>Delay (Days)</th>
                                        <th style={s.th}>Conf. Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {delayedOrders.map((o) => (
                                        <tr key={o.orderNo}>
                                            <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{o.customer}</td>
                                            <td style={s.td}>{o.orderNo}</td>
                                            <td style={s.tdRight}>{fmtKm(o.qty)}</td>
                                            <td style={{ ...s.tdRight, color: '#dc2626', fontWeight: 500 }}>{o.delay}</td>
                                            <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{o.confDate}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={s.viewAll}>View All Delayed Orders →</div>
                    </div>

                    <div style={s.card}>
                        <div style={s.cardHeader}><h3 style={s.cardTitle}>Alerts &amp; Exceptions</h3></div>
                        <div style={{ padding: '0 20px 8px' }}>
                            {alerts.map((a, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderTop: i === 0 ? 'none' : '1px solid #f3f4f6' }}>
                                    <span style={s.badge(alertStyle[a.level])}>{a.level}</span>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 12.5, color: '#374151' }}>{a.text}</div>
                                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{a.time}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div style={s.viewAll}>View All Alerts →</div>
                    </div>
                </div>

                <p style={{ textAlign: 'center', fontSize: 12, color: '#9ca3af', paddingTop: 8 }}>
                    All amounts are in ₹ Crore &nbsp;|&nbsp; km: Kilometres &nbsp;|&nbsp; Data is real-time and synchronized across all plants
                </p>
            </div>
        </Layout>
    );
}

export default COODashboard;